<script setup lang="ts">
import PipedriveUserPicture from '@components/PipedriveUserPicture.vue';
import { useSelectionItemStore } from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import Button from '@components/Button.vue';
import ButtonLink from '@components/ButtonLink.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import { flashBagSuccess } from '@tools/flashbag';
import { trans } from '@elements/translation';
import { PhoneNumber } from '@tools/phoneNumber';
import IconCopy from '@icons/ph_photocopier.svg';
import IconPhoneFill from '@icons/ph_phone-fill.svg';
import IconWhatsapp from '@icons/ph_whatsapp.svg';
import IconVisitPlanned from '@icons/ph_visit-planned.svg';
import type { AccountManager } from '@models/accountManager.ts';

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());
const { accountManagerIsOnline } = storeToRefs(useSelectionItemStore());

const props = defineProps<{ accountManager: AccountManager }>();

const copyEmail = async (): Promise<void> => {
  await navigator.clipboard.writeText(props.accountManager.email);

  flashBagSuccess(trans('selection_item.contact.email.copied'));
};

const triggerAppointment = (): void => {
  window.open(props.accountManager.calendly_url.toString(), isMobileOrTablet.value ? '_self' : '_blank');
};

const phoneNumber = computed<PhoneNumber|null>(() => (props.accountManager.phone_number ? new PhoneNumber(props.accountManager.phone_number) : null));

const whatsappNumber = computed<PhoneNumber|null>(() => (props.accountManager.whatsapp_number ? new PhoneNumber(props.accountManager.whatsapp_number) : null));
</script>

<template>
  <div class="selection-item-account-manager">
    <div class="selection-item-account-manager__profile">
      <PipedriveUserPicture
        :user="accountManager"
        :size="48"
        :is-online="accountManagerIsOnline"
      />

      <div>
        <Typography variant="body-bold" as="h4">
          {{ accountManager.firstname }}
        </Typography>

        <Typography variant="body-small" class="selection-item-account-manager__profile__details">
          <template v-if="accountManager.message">
            {{ accountManager.message }}
          </template>

          <div v-else class="selection-item-account-manager__copy" @click="copyEmail">
            <span>{{ accountManager.email }}</span>

            <Icon
              :svg="IconCopy"
              height="16"
              width="16"
            />
          </div>
        </Typography>
      </div>
    </div>

    <div class="selection-item-account-manager__ctas">
      <ButtonLink
        v-if="phoneNumber"
        class="selection-item-account-manager__cta selection-item-account-manager__phone"
        :href="`tel:${phoneNumber.international}`"
      >
        <Icon
          :svg="IconPhoneFill"
          height="24"
          width="24"
        />

        <span>
          {{ phoneNumber.printFormattedNational }}
        </span>
      </ButtonLink>

      <ButtonLink
        v-if="whatsappNumber"
        variant="circle"
        size="circle-default"
        class="selection-item-account-manager__cta"
        :href="`https://wa.me/${whatsappNumber.international}`"
        target="_blank"
      >
        <Icon
          :svg="IconWhatsapp"
          fill="green-whatsapp"
          height="24"
          width="24"
        />
      </ButtonLink>

      <Button
        v-if="accountManager.calendly_url"
        variant="circle"
        size="circle-default"
        class="selection-item-account-manager__cta"
        @click="triggerAppointment"
      >
        <Icon
          :svg="IconVisitPlanned"
          fill="black"
        />
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-account-manager {
  background-color: $colorWhite;
  border-radius: rem(8);
  border: solid 1px $colorGrayLight;
  box-shadow: 0 rem(2) rem(4) 0 $colorGrayLight;
  margin: rem(24);
  padding: rem(16);
  display: flex;
  flex-direction: column;
  gap: rem(16);
  min-width: rem(250);

  &__profile {
    display: flex;
    gap: rem(16);

    &__details {
      span {
        text-decoration: underline;
      }
    }
  }

  &__ctas {
    display: flex;
    gap: rem(8);
    width: fit-content;
  }

  &__cta {
    display: flex;
    flex-direction: row;
    gap: rem(8);
    justify-content: center;
    white-space: nowrap;
    flex: 1;

    &:not(:hover) {
      background-color: $colorWhite;
      text-decoration: none;
    }
  }

  &__phone {
    display: flex;
    align-items: center;
    width: fit-content;
    padding: rem(12) rem(16) rem(12) rem(12);
    border-radius: rem(24);
  }

  &__copy {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: rem(4);
  }

  &__phone {
    &:active {
      fill: $colorBlue;
    }
  }
}

@media screen and (min-width: $breakTablet) {
  .selection-item-account-manager {
    flex-direction: row;
    justify-content: space-between;
  }
}
</style>
